/**
 * upload.ts — Zentrale Validation für File-Uploads
 *
 * Härtet alle Supabase-Storage-Uploads gegen:
 * - Zu große Dateien (Bandbreite/Storage-Kosten)
 * - Falsche MIME-Types (z.B. .exe als .jpg getarnt)
 * - Path-Traversal-Angriffe (z.B. "../../etc/passwd" im Dateinamen)
 *
 * Nutzung:
 *   const err = validateUploadFile('kyc-documents', file);
 *   if (err) return { error: err };
 *   const safeName = sanitizeFileName(file.name);
 */

/**
 * Konfiguration für einen Storage-Bucket: Größenlimit + erlaubte MIME-Types.
 */
export interface BucketConfig {
  /** Maximale Dateigröße in Megabyte. */
  maxMb: number;
  /**
   * Erlaubte MIME-Type-Präfixe.
   * Ein Präfix-Match bedeutet: `'image/'` erlaubt `image/jpeg`, `image/png` etc.
   */
  allowed: readonly string[];
}

/**
 * Bucket-Konfiguration: Max-Size und erlaubte MIME-Type-Präfixe.
 *
 * Das `as const satisfies Record<...>` Pattern stellt sicher, dass:
 *   1. Jeder Eintrag das `BucketConfig`-Schema erfüllt (Compile-Check)
 *   2. Die Bucket-Namen als Literal-Union exportierbar sind
 *      (siehe `BucketName`)
 *   3. Die Konfiguration zur Laufzeit readonly ist (kein versehentliches
 *      Erweitern der `allowed`-Liste durch Drittcode)
 */
export const BUCKET_CONFIG = {
  'chat-bilder': {
    maxMb: 10,
    allowed: ['image/'],
  },
  'kyc-documents': {
    maxMb: 20,
    allowed: ['image/', 'application/pdf'],
  },
  'delivery-photos': {
    maxMb: 10,
    allowed: ['image/'],
  },
  'delivery-notes': {
    maxMb: 20,
    allowed: ['image/', 'application/pdf'],
  },
  'auftrag-dokumente': {
    maxMb: 20,
    allowed: [
      'image/',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
  },
  'zertifikate': {
    maxMb: 20,
    allowed: ['image/', 'application/pdf'],
  },
} as const satisfies Record<string, BucketConfig>;

/**
 * Union aller bekannten Bucket-Namen.
 * Wird automatisch aus `BUCKET_CONFIG` abgeleitet — wenn ein Bucket
 * dort hinzugefügt/entfernt wird, aktualisiert sich `BucketName`
 * automatisch und alle Caller bekommen Compile-Time-Sicherheit.
 */
export type BucketName = keyof typeof BUCKET_CONFIG;

/**
 * Strukturierter Validation-Error für i18n-Übersetzung.
 *
 * Wird vom `api()`-Wrapper an `formatStructuredError()` weitergereicht.
 * Strukturell kompatibel zur `ApiError`-Variante in `api.ts`.
 *
 * Die `code`-Werte sind i18n-Keys; weitere Felder werden als Platzhalter
 * in der übersetzten Message verwendet (z.B. `{maxMb}` → `10`).
 */
export type UploadValidationError =
  | { code: 'uploadFileMissing' }
  | { code: 'uploadFileTooLarge'; maxMb: number }
  | { code: 'uploadFileTypeNotAllowed' };

/**
 * Validiert eine `File` gegen die Bucket-Regeln (Größe + MIME-Type).
 *
 * @param bucket - Bucket-Name (Compile-Time-validiert über `BucketName`-Union)
 * @param file   - die zu prüfende Datei
 * @returns `null` wenn OK, sonst ein strukturierter Error für i18n
 */
export function validateUploadFile(
  bucket: BucketName,
  file: File | null | undefined,
): UploadValidationError | null {
  if (!file) return { code: 'uploadFileMissing' };
  const cfg = BUCKET_CONFIG[bucket];

  // Größe prüfen
  const maxBytes = cfg.maxMb * 1024 * 1024;
  if (file.size > maxBytes) {
    return { code: 'uploadFileTooLarge', maxMb: cfg.maxMb };
  }

  // MIME-Type prüfen (Präfix-Match)
  const type = file.type || '';
  const ok = cfg.allowed.some(function (prefix) {
    return type.indexOf(prefix) === 0;
  });
  if (!ok) {
    return { code: 'uploadFileTypeNotAllowed' };
  }

  return null;
}

/**
 * Sanitiert einen Dateinamen gegen Path-Traversal und gefährliche Zeichen.
 * Erlaubt: `a-z A-Z 0-9 . - _` (Rest wird durch `_` ersetzt).
 * Begrenzt auf 100 Zeichen.
 *
 * Beispiele:
 *   "../../etc/passwd"         → "_.._.._etc_passwd"
 *   "Rechnung 2026.pdf"        → "Rechnung_2026.pdf"
 *   "foto (1).jpg"             → "foto__1_.jpg"
 *
 * @param name - Original-Dateiname
 * @returns sicherer Dateiname (nie leer — Fallback `'unnamed'`)
 */
export function sanitizeFileName(name: string | null | undefined): string {
  if (!name) return 'unnamed';
  // Path-Segmente entfernen (nur letzten Teil behalten)
  const base = String(name).split(/[/\\]/).pop() || 'unnamed';
  // Nur sichere Zeichen erlauben
  const clean = base.replace(/[^a-zA-Z0-9._-]/g, '_');
  // Länge begrenzen
  return clean.slice(0, 100) || 'unnamed';
}

// ════════════════════════════════════════════════════════════════════
// MAGIC-BYTES-VALIDATION (Finding #38, 17.04.2026)
// ════════════════════════════════════════════════════════════════════
// Zweck: Zweite Verteidigungslinie gegen gefälschte `file.type`-Werte.
// Ein Angreifer kann im Browser ein File-Objekt mit beliebigem `type`
// konstruieren (z.B. HTML-Content als `image/jpeg` deklarieren). Die
// reine MIME-Type-Prüfung in `validateUploadFile()` schaut aber nur auf
// diesen client-gemeldeten Wert — eine gefälschte Datei kommt durch.
//
// Magic-Bytes sind die ersten paar Bytes einer Datei, die ihren echten
// Typ verraten. Beispiel:
//   - JPEG beginnt mit FF D8 FF
//   - PNG  beginnt mit 89 50 4E 47
//   - PDF  beginnt mit 25 50 44 46 ("%PDF")
//
// Wir lesen die ersten 16 Bytes der Datei im Browser und vergleichen
// gegen bekannte Signaturen. Passt die erkannte Signatur NICHT zum
// erwarteten Typ → Upload abgelehnt.
//
// Defense-in-Depth-Hinweis: Diese Prüfung läuft im Client und kann von
// einem Angreifer mit direkter Supabase-API-Nutzung umgangen werden.
// Der *echte* server-side-Schutz sind die `allowed_mime_types` auf den
// Storage-Buckets (konfiguriert via SQL-Migration) — die prüft der
// Supabase-Storage-Server unabhängig vom Client.

/**
 * Magic-Bytes-Signaturen (bekannte Dateiformate).
 * Pro Eintrag: erwartete Byte-Sequenz (Hex-Werte) + zugehörige MIME-Types.
 * Dateien müssen mit genau dieser Byte-Sequenz beginnen.
 */
const MAGIC_SIGNATURES: ReadonlyArray<{
  bytes: readonly number[];
  mimes: readonly string[];
}> = [
  // JPEG (alle gängigen Varianten beginnen mit FF D8 FF)
  { bytes: [0xff, 0xd8, 0xff], mimes: ['image/jpeg', 'image/jpg'] },
  // PNG
  { bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], mimes: ['image/png'] },
  // GIF87a
  { bytes: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61], mimes: ['image/gif'] },
  // GIF89a
  { bytes: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], mimes: ['image/gif'] },
  // WebP: "RIFF" .... "WEBP" — wir prüfen nur RIFF-Präfix (4 Bytes),
  // die WEBP-Sub-Kennung bei Byte 8-11 wird separat gechecked (siehe unten)
  { bytes: [0x52, 0x49, 0x46, 0x46], mimes: ['image/webp'] },
  // PDF: "%PDF-"
  { bytes: [0x25, 0x50, 0x44, 0x46, 0x2d], mimes: ['application/pdf'] },
  // DOCX/XLSX/PPTX (ZIP-basiert): "PK\x03\x04"
  { bytes: [0x50, 0x4b, 0x03, 0x04], mimes: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ] },
  // Legacy DOC/XLS: "D0 CF 11 E0 A1 B1 1A E1" (OLE Compound)
  { bytes: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1], mimes: [
    'application/msword',
    'application/vnd.ms-excel',
  ] },
];

/**
 * Prüft ob die ersten Bytes eines ArrayBuffer mit einer Signatur beginnen.
 */
function bytesMatch(haystack: Uint8Array, needle: readonly number[]): boolean {
  if (haystack.length < needle.length) return false;
  for (let i = 0; i < needle.length; i++) {
    if (haystack[i] !== needle[i]) return false;
  }
  return true;
}

/**
 * Ermittelt den echten MIME-Type anhand der Magic-Bytes.
 * Gibt `null` zurück, wenn keine bekannte Signatur passt.
 */
function detectRealMime(bytes: Uint8Array): string | null {
  for (const sig of MAGIC_SIGNATURES) {
    if (bytesMatch(bytes, sig.bytes)) {
      // WebP-Sonderfall: RIFF allein reicht nicht, wir brauchen "WEBP" bei Offset 8
      if (sig.bytes[0] === 0x52 && sig.bytes[1] === 0x49) {
        // RIFF ... WEBP
        if (bytes.length < 12) return null;
        const webpMarker = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
        if (webpMarker !== 'WEBP') {
          // RIFF-Container, aber kein WebP → ablehnen
          continue;
        }
        return 'image/webp';
      }
      // Erster Match gewinnt — MIME-Type kann je nach Kontext variieren,
      // Caller muss zusätzlich prüfen ob er in der erlaubten Liste steht.
      return sig.mimes[0];
    }
  }
  return null;
}

/**
 * Prüft Magic-Bytes gegen die erlaubten MIME-Typen des Buckets.
 *
 * Akzeptiert sowohl `File` als auch `Blob` — relevant für
 * Kamera/html2canvas-Uploads (die liefern Blob ohne Datei-Wrapper).
 * Strukturell passt Blob, weil `.slice()` und `.arrayBuffer()` auf
 * Blob definiert sind; File erbt beide Methoden.
 *
 * @param bucket Bucket-Name (wie in BUCKET_CONFIG)
 * @param file   die zu prüfende Datei oder Blob
 * @returns `null` wenn OK, sonst ein strukturierter Error für i18n
 */
async function validateMagicBytes(
  bucket: BucketName,
  file: Blob,
): Promise<UploadValidationError | null> {
  const cfg = BUCKET_CONFIG[bucket];
  try {
    // Erste 16 Bytes reichen für alle bekannten Signaturen
    const head = await file.slice(0, 16).arrayBuffer();
    const bytes = new Uint8Array(head);
    const realMime = detectRealMime(bytes);

    // Wenn wir den Typ nicht erkennen → kein Blockieren, aber auch kein Pass.
    // Grund: unbekannte Dateien können legitim sein (z.B. .txt, .csv, andere
    // Dokumentformate). Wir vertrauen dann auf die Server-Side-Validation
    // (allowed_mime_types auf dem Bucket).
    if (!realMime) return null;

    // Wenn erkannt → muss zum Bucket passen
    const ok = cfg.allowed.some((prefix) => realMime.indexOf(prefix) === 0);
    if (!ok) {
      return { code: 'uploadFileTypeNotAllowed' };
    }
    return null;
  } catch {
    // ArrayBuffer-Lesen fehlgeschlagen → blockieren
    return { code: 'uploadFileTypeNotAllowed' };
  }
}

/**
 * Strikte Validation: erst Sync-Checks (Größe + file.type), dann Magic-Bytes.
 * Diese Funktion ist ASYNC — Call-Sites müssen `await` verwenden.
 *
 * Verwende diese statt `validateUploadFile()` für jeden echten Upload.
 * Die Sync-Variante bleibt für Preview-/UI-Feedback verfügbar.
 *
 * @param bucket Bucket-Name
 * @param file   die zu prüfende Datei
 * @returns `null` wenn OK, sonst ein strukturierter Error für i18n
 */
export async function validateUploadFileDeep(
  bucket: BucketName,
  file: File | null | undefined,
): Promise<UploadValidationError | null> {
  // Erst die günstigen Sync-Checks
  const syncErr = validateUploadFile(bucket, file);
  if (syncErr) return syncErr;
  // `file` ist hier definitiv nicht null (wurde oben geprüft)
  return validateMagicBytes(bucket, file as File);
}

/**
 * Blob-Variante von `validateUploadFileDeep` für Uploads aus Kamera oder
 * html2canvas, die keinen `File`-Wrapper haben.
 *
 * Prüft:
 *   1. Existenz (Blob ist nicht null/undefined)
 *   2. Größe gegen BUCKET_CONFIG[bucket].maxMb
 *   3. Angegebener `blob.type` gegen Bucket-Whitelist
 *   4. Magic-Bytes (echter MIME-Type) gegen Bucket-Whitelist
 *
 * Ein Angreifer kann `blob.type` trivial fälschen
 * (`new Blob([malware], { type: 'image/jpeg' })`), daher ist Schritt 4
 * die eigentliche Sicherheitsebene.
 *
 * @param bucket Bucket-Name
 * @param blob   die zu prüfende Blob
 * @returns `null` wenn OK, sonst ein strukturierter Error für i18n
 */
export async function validateUploadBlobDeep(
  bucket: BucketName,
  blob: Blob | null | undefined,
): Promise<UploadValidationError | null> {
  if (!blob) return { code: 'uploadFileMissing' };
  const cfg = BUCKET_CONFIG[bucket];

  // Größe prüfen
  const maxBytes = cfg.maxMb * 1024 * 1024;
  if (blob.size > maxBytes) {
    return { code: 'uploadFileTooLarge', maxMb: cfg.maxMb };
  }

  // Client-gemeldeter MIME-Type (kann gefälscht sein)
  const type = blob.type || '';
  const prefixOk = cfg.allowed.some((prefix) => type.indexOf(prefix) === 0);
  if (!prefixOk) {
    return { code: 'uploadFileTypeNotAllowed' };
  }

  // Magic-Bytes (echter MIME-Type) — die eigentliche Sicherheitsebene
  return validateMagicBytes(bucket, blob);
}
